// [2026-10-15] ADDED — INC-279 IncAssist P3 (Proctoring builder): pure mapping from getCopilotState's `proctoring`
// part (video-feedback-system copilotProctoringExtras.buildProctoringState) to the display-only ui/ components:
// ProctoringLog entries, ScreenshotViewer shots, the AwayNudge state, the share status line and the "new" count.
// No React, no imports: tested under node (__tests__/copilotProctoringModel.test.mjs).
//
// Server entries (copilot_proctoring_log, newest first): {id, type: 'tab_away'|'ai_assisted'|'share_started'|
// 'share_ended'|'share_unsupported', kind, left_at, returned_at, away_ms, at, surface, reason, frame_url (30-minute
// signed, or null), title, title_status: 'pending'|'read'|'not_available', ai_score, snippet, question_text,
// answer_to, created_at}.

const ms = (v) => {
    if (v === null || v === undefined || v === '') return NaN;
    const t = new Date(v).getTime();
    return Number.isFinite(t) ? t : NaN;
};

/** Seconds into the interview, or undefined when the start (or the time) is unknown (the log then shows no time). */
export function secondsInto(at, startedAt) {
    const t = ms(at);
    const s = ms(startedAt);
    if (!Number.isFinite(t) || !Number.isFinite(s)) return undefined;
    return Math.max(0, Math.floor((t - s) / 1000));
}

const awaySecOf = (e) => {
    const away = Number(e && e.away_ms);
    if (Number.isFinite(away) && away > 0) return Math.max(1, Math.round(away / 1000));
    return undefined;
};

/** The tab title to show: the read title, "reading the tab name…" while pending, else none ("title not available"). */
export function titleOf(e) {
    if (!e) return undefined;
    if (e.title_status === 'read' && typeof e.title === 'string' && e.title.trim()) return e.title.trim();
    if (e.title_status === 'pending') return 'reading the tab name…';
    return undefined;
}

/**
 * ProctoringLog entries: a tab switch gives a "Switched tab" row (with its screenshot) and, once the candidate is
 * back, a "Returned to interview tab" row; an AI-assisted entry gives one row with its snippet. share_* entries are
 * not log rows (they feed shareStatus). Output order: newest first by time.
 */
export function toLogEntries(entries, startedAt) {
    const out = [];
    (Array.isArray(entries) ? entries : []).forEach((e, index) => {
        if (!e || !e.id) return;
        if (e.type === 'tab_away') {
            out.push({
                id: String(e.id),
                type: 'tab',
                t: secondsInto(e.left_at, startedAt),
                at: ms(e.left_at),
                awaySec: e.returned_at ? awaySecOf(e) : undefined,
                title: titleOf(e),
                screenshotUrl: typeof e.frame_url === 'string' && e.frame_url ? e.frame_url : undefined,
                questionText: typeof e.question_text === 'string' ? e.question_text : undefined,
                order: index,
            });
            if (e.returned_at) {
                out.push({
                    id: `${e.id}:return`,
                    type: 'return',
                    t: secondsInto(e.returned_at, startedAt),
                    at: ms(e.returned_at),
                    awaySec: awaySecOf(e),
                    order: index,
                });
            }
        } else if (e.type === 'ai_assisted') {
            const when = e.answer_to || e.created_at;
            out.push({
                id: String(e.id),
                type: 'ai',
                t: secondsInto(when, startedAt),
                at: ms(when),
                answer: typeof e.snippet === 'string' ? e.snippet : undefined,
                order: index,
            });
        }
    });
    const key = (x) => (Number.isFinite(x.at) ? x.at : -Infinity);
    return out.sort((a, b) => key(b) - key(a) || a.order - b.order).map(({ order, ...rest }) => rest);
}

/** ScreenshotViewer shots: every tab switch with a (signed) screenshot. */
export function toShots(logEntries) {
    return (Array.isArray(logEntries) ? logEntries : [])
        .filter((e) => e && e.type === 'tab' && e.screenshotUrl)
        .map((e) => ({
            id: e.id,
            src: e.screenshotUrl,
            t: e.t,
            awaySec: e.awaySec,
            questionText: e.questionText,
            alt: 'Screenshot taken after the candidate switched tab',
        }));
}

/**
 * The screen-share status from the newest share_* entry:
 *   'not_shared'  no share_started / share_unsupported has arrived (P1 review F6: e.g. joined through a path that
 *                 skipped the share step) — shown so the gap is visible;
 *   'monitor'     the entire screen is shared (nothing is shown);
 *   'unverified'  shared, but the browser did not confirm it is the entire screen (P1 review F5c);
 *   'ended'       the candidate stopped sharing;
 *   'unsupported' a phone or tablet (or no screen-share API): tab switches are logged without screenshots.
 */
export function shareStatus(entries) {
    let newest = null;
    let newestAt = -Infinity;
    (Array.isArray(entries) ? entries : []).forEach((e) => {
        if (!e || !['share_started', 'share_ended', 'share_unsupported'].includes(e.type)) return;
        const t = ms(e.at || e.created_at);
        const at = Number.isFinite(t) ? t : -Infinity;
        if (!newest || at > newestAt) {
            newest = e;
            newestAt = at;
        }
    });
    if (!newest) return { status: 'not_shared' };
    if (newest.type === 'share_unsupported') return { status: 'unsupported', reason: newest.reason };
    if (newest.type === 'share_ended') return { status: 'ended' };
    return { status: newest.surface === 'monitor' ? 'monitor' : 'unverified' };
}

export const SHARE_STATUS_TEXT = {
    not_shared:
        "Screen not shared. Screenshots and tab names aren't available until the candidate shares their screen.",
    unverified: "The browser couldn't confirm the entire screen is shared. The candidate may have shared a window.",
    ended: 'The candidate stopped sharing their screen.',
    unsupported:
        "Screen sharing isn't available on the candidate's device. Tab switches are logged without screenshots.",
};

// A "back" nudge is only shown for a return this recent (a later poll must not bring it back).
export const NUDGE_BACK_WINDOW_MS = 30 * 1000;

/** The away nudge: the newest tab switch still open → 'away' (timer from left_at), or just ended → 'back'. */
export function nudgeOf(entries, now = Date.now()) {
    let newest = null;
    (Array.isArray(entries) ? entries : []).forEach((e) => {
        if (!e || e.type !== 'tab_away' || !Number.isFinite(ms(e.left_at))) return;
        if (!newest || ms(e.left_at) > ms(newest.left_at)) newest = e;
    });
    if (!newest) return { state: null };
    const leftAt = ms(newest.left_at);
    if (!newest.returned_at) return { state: 'away', leftAt, id: String(newest.id) };
    const back = ms(newest.returned_at);
    if (Number.isFinite(back) && now - back >= 0 && now - back <= NUDGE_BACK_WINDOW_MS) {
        return { state: 'back', leftAt, awaySec: awaySecOf(newest), id: String(newest.id) };
    }
    return { state: null };
}

// [2026-10-29] ADDED — INC-2050: "N new" by event id instead of by time. A time comparison mixes two clocks (the
// candidate's browser stamps left_at, the interviewer's browser stamped the "seen" time), so a candidate clock that runs
// ahead kept every event "new" for ever. Ids need no clock.
const isFlagged = (e) => !!e && !!e.id && (e.type === 'tab_away' || e.type === 'ai_assisted');

/**
 * The ids of the flagged entries in `entries`, added to `seenIds` (id → true). Never null: a first view with no events
 * gives {} ("seen"). Returns `seenIds` itself when nothing is added (so the reducer can skip an unchanged state).
 */
export function addSeenIds(seenIds, entries) {
    const base = seenIds && typeof seenIds === 'object' ? seenIds : {};
    let next = base;
    (Array.isArray(entries) ? entries : []).forEach((e) => {
        if (!isFlagged(e) || next[String(e.id)]) return;
        if (next === base) next = { ...base };
        next[String(e.id)] = true;
    });
    return next;
}

/**
 * The badge's and the chip's "N new". Before the Proctoring tab was ever seen (`seenIds` null) every flagged event is
 * new (`total`, the server's counts.tab + counts.ai); after it, the flagged entries whose id was not seen.
 */
export function newFlaggedByIds(entries, seenIds, total = 0) {
    if (!seenIds || typeof seenIds !== 'object') return Math.max(0, Number(total) || 0);
    return (Array.isArray(entries) ? entries : []).filter((e) => isFlagged(e) && !seenIds[String(e.id)]).length;
}

/** Flagged entries (tab switches and AI-assisted answers) newer than `seenAt`: the badge's and the chip's "N new". */
export function newFlaggedSince(entries, seenAt) {
    const since = ms(seenAt);
    if (!Number.isFinite(since)) return 0;
    return (Array.isArray(entries) ? entries : []).filter((e) => {
        if (!e || (e.type !== 'tab_away' && e.type !== 'ai_assisted')) return false;
        const t = ms(e.type === 'tab_away' ? e.left_at : e.answer_to || e.created_at);
        return Number.isFinite(t) && t > since;
    }).length;
}
